/**
 * Plain-date helpers, in the visitor's own calendar.
 *
 * `toISOString().split("T")[0]` is the trap these exist to avoid: it converts to
 * UTC before formatting, so for anyone east of Greenwich a date carrying an
 * evening time serialises as the *next* day. On a booking form that means a
 * customer taps Monday at 22:00 and books Tuesday.
 */

/** yyyy-MM-dd as the calendar on the wall reads it. */
export const toISODate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const today = () => toISODate(new Date());

export const tomorrow = () => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return toISODate(date);
};

/**
 * A yyyy-MM-dd string as a Date in the local calendar.
 *
 * Built from the parts rather than passed to `new Date(iso)`, which reads a
 * bare date as UTC midnight — a day earlier than intended for anyone west of
 * Greenwich.
 */
export const fromISODate = (iso) => {
  const [year, month, day] = (iso || "").split("-").map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
};

/** "Thu, 21 Aug" in the given language. */
export const formatDay = (iso, locale = "en") =>
  fromISODate(iso).toLocaleDateString(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });

/**
 * A booking's `bookingDateTime` as the clock on the wall reads it.
 *
 * `Booking.BookingDateTime` holds the local time the customer picked, stamped
 * `DateTimeKind.Utc` only so Npgsql will write it to `timestamptz`. It therefore
 * serialises with a `Z` it does not mean. `new Date(value)` believes that `Z`
 * and shifts by the viewer's offset, so a 10:00 appointment renders as 13:00 in
 * Bucharest — the same trap `toISOString()` sets, in the other direction.
 *
 * Reading the components straight out of the string and rebuilding them in local
 * terms keeps the wall clock intact. Every human-facing surface wants this;
 * nothing wants the raw `new Date`.
 */
export const fromWallClock = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;

  const parts = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(
    String(value),
  );
  // Anything that isn't the shape we stamp is left to the platform parser
  // rather than silently turned into an Invalid Date.
  if (!parts) return new Date(value);

  const [, year, month, day, hour, minute, second] = parts;
  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hour),
    Number(minute),
    Number(second || 0),
  );
};

/** The wall-clock date of a booking as `yyyy-MM-dd`, for date inputs. */
export const wallClockISODate = (value) => {
  const date = fromWallClock(value);
  return date ? toISODate(date) : "";
};

/**
 * The wall-clock time of a booking as `HH:mm`, matching the 24-hour half of the
 * slot strings the API accepts back.
 */
export const wallClockTime = (value) => {
  const date = fromWallClock(value);
  if (!date) return "";
  return `${String(date.getHours()).padStart(2, "0")}:${String(
    date.getMinutes(),
  ).padStart(2, "0")}`;
};

/**
 * A booking's time as the slot string the API speaks: 24-hour `HH:mm` with a
 * decorative AM/PM suffix pinned to it ("16:00 PM").
 *
 * Mirrors `AvailabilityServices` exactly, because the slot dropdowns are keyed
 * by these strings — a prefilled value in any other shape matches no option and
 * the select silently shows blank.
 */
export const toSlotString = (value) => {
  const date = fromWallClock(value);
  if (!date) return "";
  const hours = date.getHours();
  return `${String(hours).padStart(2, "0")}:${String(date.getMinutes()).padStart(
    2,
    "0",
  )} ${hours < 12 ? "AM" : "PM"}`;
};

/**
 * A slot string as a human should read it.
 *
 * The API's slot format is a wire contract, not a display choice: 24-hour with
 * a decorative AM/PM suffix pinned to it, so "16:00 PM" and "13:00 PM" are both
 * correct on the wire and both nonsense on a button. Strip the suffix and show
 * the 24-hour reading, matching how every other time in the app is displayed.
 *
 * Always keep the raw string as the value you post back — only the label changes.
 */
export const formatSlotLabel = (slot) => {
  if (!slot) return "";
  const match = /^(\d{1,2}):(\d{2})/.exec(String(slot).trim());
  if (!match) return String(slot);
  return `${match[1].padStart(2, "0")}:${match[2]}`;
};

/** "Aug 27, 2026, 10:00" — a booking's full wall-clock stamp for display. */
export const formatWallClockDateTime = (value, locale = "en") => {
  const date = fromWallClock(value);
  if (!date) return "-";
  return date.toLocaleString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
};

/**
 * Whether a booking's wall-clock time has already passed.
 *
 * Compared against the viewer's own clock, which is exact while they sit in the
 * business's timezone — the case this platform is built for — and drifts by the
 * difference when they don't. The booking payload carries no timezone to do
 * better with; the authoritative answer lives server-side, where
 * `OrganizationClock` has the organization's own zone.
 */
export const isWallClockPast = (value, endOffsetMinutes = 0) => {
  const date = fromWallClock(value);
  if (!date) return false;
  return date.getTime() + endOffsetMinutes * 60000 <= Date.now();
};
